import * as types from '../constants/cidraActions';
import ProxListenerManager from '../services/ProxListenerManager';
import { AuthenticationProtocols } from '../constants/clientProtocols';
import { decideNextStep } from './decideNextStep';
import {
  startLoading, setServerState, doSecondFactorFailure, doLogout
} from './shared';
import { doExcludeModality } from './chooseModality';

/**
 * Initialize proximity card reader detection
 * Checks if reader is available and updates Redux state
 * API v2: init() is now async and returns a Promise
 */
export function initProxReader() {
  return async function(dispatch, getState) {
    const manager = ProxListenerManager.getInstance({ dispatch, getState, });
    const connected = await manager.init();

    dispatch({
      type: types.PROX_READER_STATUS,
      payload: { connected, },
    });

    return connected;
  };
}

/**
 * Start proximity subscription (badge taps + reader connect/disconnect)
 * API v3: subscribe() queries for initial reader state
 * Note: Name kept as startProxDeviceWatch for backward compatibility
 */
export function startProxDeviceWatch() {
  return async function(dispatch, getState) {
    const manager = ProxListenerManager.getInstance({ dispatch, getState, });
    await manager.subscribe();
  };
}

/**
 * Stop proximity subscription
 * API v2: unsubscribe() replaces separate stops for badge listening and device watching
 * Note: Name kept as stopProxDeviceWatch for backward compatibility
 */
export function stopProxDeviceWatch() {
  return function(dispatch, getState) {
    const manager = ProxListenerManager.getInstance({ dispatch, getState, });
    manager.unsubscribe();
  };
}

/**
 * Handle reader disconnection event.
 * If the server supports the excludeModality capability, asks it to exclude
 * proxcard and decide the next step; otherwise just removes proxcard from
 * the available modalities.
 */
export function handleReaderDisconnected() {
  return function(dispatch, getState) {
    const { capabilities, proxReaderConnected, } = getState().login;
    if (proxReaderConnected) {
      const modality = 'proxcard';
      if (capabilities?.CommonAuthenticationUI?.some(capability => capability.capabilityName === 'excludeModality') ) {
        dispatch(doExcludeModality(modality, removeProxCardModality) );
      }
      else {
        dispatch(removeProxCardModality() );
      }
    }
  };
}

export function removeProxCardModality() {
  return { type: types.PROX_READER_DISCONNECTED, };
}

/**
 * Verify proximity card credentials with server
 * @param {string} cardId - Badge card ID
 * @param {function} failure - Failure callback.
 */
export function verifyProxCard(cardId, failure) {
  return function(dispatch, getState) {
    const {
      authClient, oneSignSessionId, session,
    } = getState().login;

    dispatch(startLoading() );

    // Encrypt card credentials using session encryption
    const encryptedCardId = session.encryptSecret(cardId);

    // Create prox card authentication request
    const { AuthVerifyProxCard, AuthResponseAuthErrorContinued, } = AuthenticationProtocols;
    const request = new AuthVerifyProxCard(oneSignSessionId, encryptedCardId.value);

    return authClient.sendRequest(request).then(response => {
      const serverState = response.message;

      dispatch(setServerState(serverState) );

      if (serverState.errorCode && serverState.responseType !== AuthResponseAuthErrorContinued) {
        dispatch(doLogout() );
        if (failure) {
          dispatch(failure);
        }
        else {
          dispatch(doSecondFactorFailure(serverState.errorCode) );
        }
      }
      else {
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

/**
 * Cleanup proximity card listeners and handlers
 * Should be called on app unload (beforeunload event)
 */
export function cleanupProxListeners() {
  return function(dispatch, getState) {
    const manager = ProxListenerManager.getInstance({ dispatch, getState, });
    manager.cleanup();
  };
}
